import { defineComponent as _defineComponent } from 'vue'
import { Location, LocationCollection } from '@/models/Location';
import { Dayjs } from 'dayjs';
import { Ref, onMounted, reactive, ref, watchEffect } from 'vue';
import AddressComboListItem from '../address/AddressComboListItem.vue';
import GenreGroupSelect from '@/components/genre_group/select.vue';
import DataProviderSelect from '@/components/data_provider/Select.vue';
import TrueFalseCombo from '@/components/TrueFalseCombo.vue';
import { EventOrganizer, EventOrganizerCollection } from '@/models/EventOrganizer';
import StatusToggleBtns from './StatusToggleBtns.vue';
import ActionBtn from '../buttons/ActionBtn.vue';
import KpTooltip from '../KpTooltip.vue';
import LabelField from '../form/LabelField.vue';
import { errToStr, fieldRuleValue, formRules } from '@/helpers/tools';
import TimeTextfield from '../form/TimeTextfield.vue';
import LocationSelectField from '@/components/location/SelectField.vue';
import { commitToast } from '@/plugins/toast';
import { instance as api } from '@/helpers/api';
import { Event } from '@/models/Event';
import { globalParam } from '@/helpers/globals';
import AutocompleteDisplay from '../form/AutocompleteDisplay.vue';
import IconBtn from '../IconBtn.vue';
import DateTextfield from '../form/DateTextfield.vue';
import EventClassSelect from './EventClassSelect.vue';

type SearchParams = {
    event_status?: string[];
    schedule_date_from?: Dayjs | null;
    schedule_date_to?: Dayjs | null;
    ort?: string | null;
    location_id?: number | null;
    location_text?: string | null;
    event_organizer_id?: number | null;
    genre_group_ids?: Array<number> | null;
    event_class: string;
    title?: string | null;
    for_print?: boolean | null;
    for_website?: boolean | null;
    is_featured?: boolean | null;
    edited?: boolean | null;
    cancelled?: boolean | null;
    bp_import_uuid?: Array<string> | null;
    own_staff?: boolean | null;
};

export default /*#__PURE__*/_defineComponent({
  __name: 'CompactSearchBar',
  props: {
    value: null
  },
  emits: ['input', 'new-event', 'reset'],
  setup(__props: any, { emit }) {

const props = __props as {
    value: SearchParams;
};



const initialSearchParams: SearchParams = {
    event_status: [],
    schedule_date_from: null,
    schedule_date_to: null,
    ort: null,
    location_id: null,
    location_text: null,
    event_organizer_id: null,
    genre_group_ids: [],
    event_class: '',
    title: null,
    for_print: null,
    is_featured: null,
    cancelled: false,
    bp_import_uuid: [],
    edited: null,
    own_staff: false,
};



const newEventFormValid = ref(true);
const mode = ref<'normal' | 'new-event'>('normal');
const loading = ref(false);

const newEventData = reactive({
    start_date: null as Dayjs | null,
    end_date: null as Dayjs | null,
    event_time_start: undefined as string | undefined,
    location_id: undefined as number | undefined,
    main_genre_group_id: null as number | null,
    title: null as string | null,
    url: null as string | null,
    abstract: null as string | null,
    summary_text: null as string | null,
    // we set the print and website flag by default, see #27672:
    for_print: true,
    for_website: true,
    event_class: Event.CLASS_EVENT,
});

const locationSelected: Ref<Location | null> = ref(null);
const locationCollection = new LocationCollection();
locationCollection.addExtraParam('showAllLocations', true);

const eoSelected: Ref<EventOrganizer | null> = ref(null);
const eoCollection = new EventOrganizerCollection();
const eoMandatory = fieldRuleValue('field_rules.mandatory.event.organizer_id', false);
const fromTextField: Ref<HTMLElement | null> = ref(null);

watchEffect(() => {
    newEventData.start_date = props.value.schedule_date_from as any;
    newEventData.end_date = props.value.schedule_date_to as any;
    newEventData.location_id = props.value.location_id || undefined;
    if (!props.value.location_id) {
        locationSelected.value = null;
    }
});

onMounted(async () => {
    if (props.value?.location_id) {
        await locationCollection.query({
            filter: JSON.stringify({ property: 'id', value: props.value.location_id }),
        });
        locationSelected.value = (locationCollection.first() as Location | undefined) || null;
    }
    if (fromTextField.value) {
        fromTextField.value.focus();
    }
});

function onNewEventBtnClick() {
    // reset all search params when the new event panel is shown:
    emit('input', { ...initialSearchParams });
    mode.value = 'new-event';
}
function onCancelNewEventBtnClick() {
    mode.value = 'normal';
}
function onStatusChange(val: string | null) {
    emit('input', { ...props.value, ...{ event_status: val } });
}
function onDateFromChange(val: Dayjs) {
    emit('input', { ...props.value, ...{ schedule_date_from: val } });
}
function onDateToChange(val: Dayjs) {
    emit('input', { ...props.value, ...{ schedule_date_to: val } });
}
function onCityChange(val: string | null) {
    emit('input', { ...props.value, ...{ ort: val } });
}
function onLocationSelected(val: Location | null) {
    locationSelected.value = val;
    emit('input', { ...props.value, ...{ location_id: val?.id || null } });
}
function onLocationTextChange(val: string | null) {
    emit('input', { ...props.value, ...{ location_text: val || null } });
}
function onEoSelected(val: EventOrganizer | null) {
    eoSelected.value = val;
    emit('input', { ...props.value, ...{ event_organizer_id: val?.id || null } });
}
function onGenreGroupChange(val: Array<number> | null) {
    emit('input', { ...props.value, ...{ genre_group_ids: val || [] } });
}
function onEventClassChange(val: string) {
    emit('input', { ...props.value, ...{ event_class: val || '' } });
}
function onTitleChange(val: string | null) {
    emit('input', { ...props.value, ...{ title: val } });
}
function onPrintChange(val: boolean | null) {
    emit('input', { ...props.value, ...{ for_print: val } });
}
function onWebsiteChange(val: boolean | null) {
    emit('input', { ...props.value, ...{ for_website: val } });
}
function onTipChange(val: boolean | null) {
    emit('input', { ...props.value, ...{ is_featured: val } });
}
function onEditedChange(val: boolean | null) {
    emit('input', { ...props.value, ...{ edited: val } });
}
function onCancelledChange(val: boolean | null) {
    emit('input', { ...props.value, ...{ cancelled: val } });
}
function onOwnStaffChange(val: boolean | null) {
    emit('input', { ...props.value, ...{ own_staff: val } });
}

function onResetBtnClick() {
    if (fromTextField.value) {
        fromTextField.value.focus();
    }
    emit('reset');
}

function onDataProviderChange(val: string | null) {
    emit('input', { ...props.value, ...{ bp_import_uuid: val ?? [] } });
}

async function createNewEventBtn() {
    if (newEventFormValid.value) {
        loading.value = true;
        try {
            const res = await api().post('/Event/quickCreate', newEventData);
            const eventData = res?.data?.event;
            // reset input form selectively:
            newEventData.title = null;
            newEventData.abstract = null;
            newEventData.summary_text = null;
            newEventData.url = null;
            newEventData.event_class = Event.CLASS_EVENT;
            newEventData.main_genre_group_id = null;
            // close edit form:
            mode.value = 'normal';
            // emit new event
            emit('new-event', new Event(eventData));
        } catch (e) {
            commitToast({ message: errToStr(e), color: 'error' });
        } finally {
            loading.value = false;
        }
    }
}

return { __sfc: true,emit, initialSearchParams, props, newEventFormValid, mode, loading, newEventData, locationSelected, locationCollection, eoSelected, eoCollection, eoMandatory, fromTextField, onNewEventBtnClick, onCancelNewEventBtnClick, onStatusChange, onDateFromChange, onDateToChange, onCityChange, onLocationSelected, onLocationTextChange, onEoSelected, onGenreGroupChange, onEventClassChange, onTitleChange, onPrintChange, onWebsiteChange, onTipChange, onEditedChange, onCancelledChange, onOwnStaffChange, onResetBtnClick, onDataProviderChange, createNewEventBtn, AddressComboListItem, GenreGroupSelect, DataProviderSelect, TrueFalseCombo, StatusToggleBtns, ActionBtn, KpTooltip, LabelField, formRules, TimeTextfield, LocationSelectField, globalParam, AutocompleteDisplay, IconBtn, DateTextfield, EventClassSelect }
}

})